/**
 * Velle Private page (templates/page.liquid, handle `velle-private`).
 *
 * <s-private-gate data-salt="number">
 *   Vault entry with a personal member number + password. Both come from the member's Shopify
 *   customer ID and are emailed by a Shopify Messaging automation (see DESIGN.md, Velle Private):
 *     number   = 4-digit member number (0002, 0003…) assigned by Flow, saved in customer metafield custom.velle_socio
 *     password = ((number mod 900000) × 7919 + salt) mod 900000 + 100000  (6 digits)
 *   The page only checks that the password matches the number, so the email Liquid and
 *   data-salt must use the same values. Soft gate: client-side only, not real access control.
 *   Wrong → data-vault="error" (form shake + message) and the thief act (data-thief walk/try/talk/leave). Right → data-vault="open" (handle spins,
 *   bolts retract, door swings, zoom in), then data-state="member" shows the members view.
 *   [data-vault-close] ("Fechar cofre") plays it in reverse (data-flash, then data-vault="closing").
 *   The vault never stays unlocked: the password is asked on every visit. Only the member number
 *   is remembered (localStorage velle:private:member) and prefilled.
 *   [data-join-form] is the Shopify customer form; [data-joined] is shown after a successful join.
 *
 * <s-private-drop data-drop-at="unix seconds">: live countdown. Fills [data-d] [data-h] [data-m]
 * [data-s]; when the time is reached sets `data-live` on the host so CSS reveals the pieces.
 */
const MEMBER_KEY = 'velle:private:member';
const OPEN_DURATION = 3800;
const FLASH_DURATION = 500;
const CLOSE_DURATION = 4200;
const MULTIPLIER = 7919;

function store(key, value) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}

function read(key) {
  try {
    return localStorage.getItem(key) || '';
  } catch {
    return '';
  }
}

function expectedPassword(memberId, salt) {
  const base = Number(BigInt(memberId) % 900000n);
  return String(((base * MULTIPLIER + salt) % 900000) + 100000);
}

class PrivateGate extends HTMLElement {
  connectedCallback() {
    this.salt = Number(this.dataset.salt) || 0;
    this.showNumber(read(MEMBER_KEY));

    this.vaultForm = this.querySelector('[data-vault-form]');
    // Only the member number is remembered; the password is asked on every visit.
    const memberInput = this.vaultForm?.querySelector('input[name="member"]');
    const savedNumber = read(MEMBER_KEY);
    if (memberInput && savedNumber) memberInput.value = savedNumber;
    this.vaultForm?.addEventListener('submit', (event) => this.tryOpen(event));
    this.querySelector('[data-vault-close]')?.addEventListener('click', () => this.close());
  }

  /* Opening in reverse: flash in, swap back to the locked view (still in the open pose),
     then data-vault="closing" lets every part transition back with reversed delays. */
  close() {
    if (this.dataset.vault !== 'open') return;
    this.toggleAttribute('data-flash', true);
    setTimeout(() => {
      window.scrollTo({ top: 0 });
      delete this.dataset.state;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.removeAttribute('data-flash');
          this.dataset.vault = 'closing';
        });
      });
      setTimeout(() => {
        delete this.dataset.vault;
        const password = this.vaultForm?.querySelector('input[name="password"]');
        this.vaultForm?.querySelector('button[type="submit"]')?.removeAttribute('disabled');
        if (password) {
          password.value = '';
          password.focus({ preventScroll: true });
        }
      }, CLOSE_DURATION);
    }, FLASH_DURATION);
  }


  /* Wrong details: the thief walks in, tugs the vault handle, fails, turns to the visitor and
     talks, then walks off. A new attempt restarts him; reduced motion shows him standing. */
  async thiefAttempt() {
    const thief = this.querySelector('[data-thief]');
    if (!thief) return;
    const run = (this.thiefRun = (this.thiefRun || 0) + 1);
    this.resetThief(thief);
    const figure = thief.querySelector('.thief-figure');
    const handle = this.querySelector('.vault-handle');
    const vault = this.querySelector('.vault-zoom');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    if (vault && vault.getBoundingClientRect().top < 0) vault.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });

    if (reduce) {
      this.dataset.thief = 'talk';
      this.thiefTimer = setTimeout(() => this.thiefRun === run && this.resetThief(thief), 7000);
      return;
    }

    this.dataset.thief = 'walk';
    const walk = thief.animate(
      [{ transform: 'translateX(-60vw)' }, { transform: 'translateX(0)' }],
      { duration: 1600, easing: 'cubic-bezier(0.25, 0.6, 0.3, 1)', fill: 'both' }
    );
    await walk.finished.catch(() => {});
    if (this.thiefRun !== run) return;

    this.dataset.thief = 'try';
    const tug = { duration: 1000, easing: 'ease-in-out' };
    handle?.animate([{ rotate: '0deg' }, { rotate: '22deg' }, { rotate: '-8deg' }, { rotate: '28deg' }, { rotate: '-4deg' }, { rotate: '0deg' }], tug);
    vault?.animate([{ translate: '0' }, { translate: '-4px' }, { translate: '3px' }, { translate: '-3px' }, { translate: '2px' }, { translate: '0' }], tug);
    figure?.animate([{ translate: '0' }, { translate: '-3px' }, { translate: '1px' }, { translate: '-3px' }, { translate: '1px' }, { translate: '0' }], tug);
    await pause(1150);
    if (this.thiefRun !== run) return;

    this.dataset.thief = 'talk';
    figure?.animate([{ scale: '1 1' }, { scale: '0.82 1' }, { scale: '1 1' }], { duration: 260, easing: 'ease-in-out' });
    this.thiefTimer = setTimeout(async () => {
      if (this.thiefRun !== run) return;
      this.dataset.thief = 'leave';
      const leave = thief.animate(
        [{ transform: 'translateX(0)' }, { transform: 'translateX(-60vw)' }],
        { duration: 1100, easing: 'cubic-bezier(0.5, 0, 0.75, 0)', fill: 'both' }
      );
      await leave.finished.catch(() => {});
      if (this.thiefRun === run) this.resetThief(thief);
    }, 6000);
  }

  resetThief(thief = this.querySelector('[data-thief]')) {
    clearTimeout(this.thiefTimer);
    delete this.dataset.thief;
    thief?.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
  }

  showNumber(number) {
    if (!number) return;
    this.querySelectorAll('[data-member-number]').forEach((node) => {
      node.textContent = number;
    });
  }

  tryOpen(event) {
    event.preventDefault();
    const data = new FormData(this.vaultForm);
    const number = String(data.get('member') || '').replace(/\D/g, '');
    const password = String(data.get('password') || '').replace(/\s/g, '');
    const message = this.querySelector('[data-vault-message]');

    const numberOk = /^\d{4}$/.test(number) && Number(number) > 0;
    const passwordOk = numberOk && password === expectedPassword(number, this.salt);

    if (!numberOk || !passwordOk) {
      this.dataset.vault = '';
      void this.offsetWidth; // restart the shake animation
      this.dataset.vault = 'error';
      if (message) message.hidden = false;
      this.thiefAttempt();
      return;
    }

    if (message) message.hidden = true;
    this.thiefRun = (this.thiefRun || 0) + 1;
    this.resetThief();
    store(MEMBER_KEY, number);
    this.showNumber(number);
    this.dataset.vault = 'open';
    this.vaultForm.querySelector('button[type="submit"]')?.setAttribute('disabled', '');
    // Bring the vault into view so the opening is seen, then reveal the members view.
    this.querySelector('.vault-zoom')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      this.dataset.state = 'member';
      window.scrollTo({ top: 0 });
    }, OPEN_DURATION);
  }
}

class PrivateDrop extends HTMLElement {
  connectedCallback() {
    this.target = Number(this.dataset.dropAt) * 1000;
    if (!this.target) return;
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const left = Math.max(0, this.target - Date.now());
    const seconds = Math.floor(left / 1000);
    const parts = {
      d: Math.floor(seconds / 86400),
      h: Math.floor((seconds % 86400) / 3600),
      m: Math.floor((seconds % 3600) / 60),
      s: seconds % 60,
    };
    for (const [key, value] of Object.entries(parts)) {
      this.querySelectorAll(`[data-${key}]`).forEach((node) => {
        node.textContent = String(value).padStart(2, '0');
      });
    }
    if (left === 0) {
      this.toggleAttribute('data-live', true);
      clearInterval(this.timer);
    }
  }
}

if (!customElements.get('s-private-gate')) customElements.define('s-private-gate', PrivateGate);
if (!customElements.get('s-private-drop')) customElements.define('s-private-drop', PrivateDrop);
